import { useState, useMemo } from 'react';
import { usePopper } from 'react-popper';
import { useDeepEqualMemo } from '@wix/da-hooks/pkg/useDeepEqualMemo';
import { useDebugRerenders } from '@wix/da-hooks/pkg/useDebugRerenders';
import { convertFromPopperPlacement, convertToPopperPlacement } from '../helpers';
import sassVariables from '../../../styles/variables.scss';
const wrapperMaxWidth = parseInt(sassVariables['wrapper_max_width']);
/** This is our base popper wrapper that applies our custom modifiers etc */ export function useBasePopper({ disablePlacementModifier, placement, secondaryPlacement, referenceElement, positionFixed, eventsEnabled = true, offsetModifier: offsetModifierProp, allowFlip = true }) {
    var _state;
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const memoizedOffsetModifierProp = useDeepEqualMemo(offsetModifierProp);
    /* This modifier will force the popover to not be centered when it reaches
    the max width, as per designers request. By default it sets the `start`
    placement variant when that is the case, but if the popover doesn't fit
    the built-in `flip` modifier will kick in and position it accordingly. */ const overridePlacement = useMemo(()=>({
            name: 'overridePlacement',
            enabled: !disablePlacementModifier,
            phase: 'beforeMain',
            requiresIfExists: [
                'offsets',
                'flip'
            ],
            fn: ({ state })=>{
                const { width } = state.rects.popper;
                const maxWidth = disablePlacementModifier ? Number.POSITIVE_INFINITY : wrapperMaxWidth;
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const { placement, secondaryPlacement } = convertFromPopperPlacement(state.placement);
                if (width >= maxWidth && !secondaryPlacement) {
                    state.placement = convertToPopperPlacement(placement, 'start');
                    state.reset = true;
                }
            }
        }), [
        disablePlacementModifier
    ]);
    const arrowModifier = useMemo(()=>({
            name: 'arrow',
            enabled: !!arrowElement,
            options: {
                element: arrowElement
            }
        }), [
        arrowElement
    ]);
    const eventListenerModifier = useMemo(()=>({
            name: 'eventListeners',
            enabled: eventsEnabled,
            options: {
                scroll: true,
                resize: true
            }
        }), [
        eventsEnabled
    ]);
    const offsetModifier = useMemo(()=>({
            name: 'offset',
            options: {
                offset: memoizedOffsetModifierProp
            }
        }), [
        memoizedOffsetModifierProp
    ]);
    const flipModifier = useMemo(()=>({
            name: 'flip',
            enabled: allowFlip
        }), [
        allowFlip
    ]);
    const memoizedModifiers = useDeepEqualMemo([
        overridePlacement,
        arrowModifier,
        eventListenerModifier,
        offsetModifier,
        flipModifier
    ]);
    const { styles, attributes, update, state } = usePopper(referenceElement, popperElement, {
        placement: convertToPopperPlacement(placement, secondaryPlacement),
        strategy: positionFixed ? 'fixed' : 'absolute',
        modifiers: memoizedModifiers
    });
    useDebugRerenders('useBasePopper', {
        disablePlacementModifier,
        placement,
        secondaryPlacement,
        referenceElement,
        positionFixed,
        eventsEnabled,
        offsetModifier,
        allowFlip,
        popperElement,
        arrowElement
    });
    const { placement: effectivePlacement, secondaryPlacement: effectiveSecondaryPlacement } = convertFromPopperPlacement((_state = state) === null || _state === void 0 ? void 0 : _state.placement);
    return {
        rootRef: setPopperElement,
        arrowRef: setArrowElement,
        contentStyle: styles.popper,
        arrowStyle: styles.arrow,
        contentAttributes: attributes.popper,
        arrowAttributes: attributes.arrow,
        effectivePlacement,
        effectiveSecondaryPlacement,
        updatePositioning: update !== null && update !== void 0 ? update : undefined
    };
}
